import { css } from '@emotion/css';
import { Box } from '@mui/material';
import {
  EdgeLabelRenderer,
  useReactFlow,
  type XYPosition,
} from '@xyflow/react';
import { type PointerEvent, useState } from 'react';

import { EdgeLabel } from '@/components/diagrams/components/EdgeLabel';
import { InlineTextEditor } from '@/components/diagrams/components/InlineTextEditor/InlineTextEditor';
import { DiagramEdgeRoute } from '@/components/diagrams/diagramStyle';
import {
  ArrowEdge,
  type ArrowEdgeProps,
} from '@/components/diagrams/FlowChart/edges/ArrowEdge';
import { getOrthogonalPathLabelPoint } from '@/components/diagrams/FlowChart/edges/orthogonalPath';
import {
  getNearestFraction,
  getNearestSegmentIndex,
  getPointAtFraction,
} from '@/components/diagrams/FlowChart/edges/pathFraction';
import {
  getRelationshipLabelSize,
  RELATIONSHIP_NODE_WIDTH,
} from '@/components/diagrams/FlowChart/nodes/RelationshipLabelNode/relationshipLabelSize';
import { COLORS } from '@/styles/tokens/colors';

import { useDiagramEditing } from '../../contexts/diagramEditing.context';
import {
  dragLineHandle,
  getElbowRoute,
  getLineHandles,
  type LineLabelBox,
} from '../../utils/diagramLineGeometry';
import { type ReactFlowDiagramEdgeData } from '../../utils/reactFlowDiagram';
import {
  type LineDrag,
  type StartLineDrag,
  useLineDrag,
} from './hooks/useLineDrag';
import { LineControlPoint } from './LineControlPoint';

/** A label has the width of the relationship labels of the other diagrams. */
const LABEL_WIDTH = RELATIONSHIP_NODE_WIDTH;
const LABEL_CLASSES = { root: css({ width: LABEL_WIDTH }) };
/** A dragged label stays this fraction away from the ends of its line. */
const LABEL_END_MARGIN = 0.05;
/** A drag on a line this near a bend, in canvas pixels, moves the bend. */
const BEND_GRAB_DISTANCE = 10;
/** The cursor for each handle type. */
const HANDLE_CURSOR = {
  x: 'move',
  y: 'move',
  add: 'copy',
  bend: 'move',
};

/**
 * A builder line: the shared arrow, with route handles and a label that the
 * user drags along the line without first selecting it. A double-click on
 * the label edits it in place. A drag saves its result as one edit when the
 * user releases the pointer.
 *
 * A stable SVG group captures the pointer while the shared arrow changes
 * its path during a drag.
 */
export function DiagramLineEdge(props: ArrowEdgeProps) {
  const { id, data, selected } = props;
  const { screenToFlowPosition } = useReactFlow();
  const { locked, editEdge } = useDiagramEditing();
  const { drag, startDrag } = useLineDrag({
    edgeID: id,
    selected: !!selected,
    disabled: locked,
  });
  const [hoverCursor, setHoverCursor] = useState<string | null>(null);
  const [labelHovered, setLabelHovered] = useState(false);
  const { label, labelFraction } = data as ReactFlowDiagramEdgeData;

  const route = drag?.kind === 'route' ? drag.waypoints : data.waypoints;
  const isElbow =
    (data.route ?? DiagramEdgeRoute.Elbow) === DiagramEdgeRoute.Elbow;
  const ends = {
    source: { x: props.sourceX, y: props.sourceY },
    sourceSide: props.sourcePosition,
    target: { x: props.targetX, y: props.targetY },
    targetSide: props.targetPosition,
  };
  // The points of the drawn line. An elbow line without saved bends follows
  // the automatic layout route, when it has one.
  let points: XYPosition[];
  if (isElbow && !route?.length && data.routeWaypoints?.length) {
    points = [ends.source, ...data.routeWaypoints, ends.target];
  } else if (isElbow) {
    points = getElbowRoute(ends, route ?? []);
  } else {
    points = [ends.source, ...(route ?? []), ends.target];
  }
  const shownFraction =
    drag?.kind === 'label' ? drag.fraction : (labelFraction ?? undefined);
  const labelPosition =
    shownFraction === undefined
      ? data.labelPosition
      : getPointAtFraction(points, shownFraction);
  // Without a set position, the shared arrow draws the label at the middle
  // of the longest segment. A curved line without bends draws it on the
  // curve, near that point.
  const labelBox: LineLabelBox | undefined = label
    ? {
        center: labelPosition ?? getOrthogonalPathLabelPoint(points),
        ...getRelationshipLabelSize(label),
      }
    : undefined;

  /**
   * Returns the handle of the line segment under a pointer event on the line,
   * or null on a segment that does not move. Returns undefined for events
   * from the label and handles, which come through the React portal and have
   * their own handlers.
   */
  const getSegmentHandle = (event: PointerEvent<SVGGElement>) => {
    if (locked || !(event.target instanceof SVGPathElement)) return undefined;
    const index = getNearestSegmentIndex(
      points,
      screenToFlowPosition({ x: event.clientX, y: event.clientY })
    );
    // A segment without a handle, such as an end leg of an elbow, does not move.
    return (
      getLineHandles(points, isElbow).find(
        (handle) => handle.index === index
      ) ?? null
    );
  };

  return (
    <g
      className="nopan"
      style={{
        cursor: locked
          ? undefined
          : (hoverCursor ?? (isElbow ? 'default' : 'move')),
      }}
      onPointerMove={(event) => {
        // Keep the cursor while the user drags with a pressed button.
        if (event.buttons !== 0) return;
        const handle = getSegmentHandle(event);
        if (handle === undefined) return;
        setHoverCursor(
          handle ? HANDLE_CURSOR[handle.axis ?? 'bend'] : 'default'
        );
      }}
      onPointerLeave={() => setHoverCursor(null)}
      onPointerDown={(event) => {
        const handle = getSegmentHandle(event);
        if (!handle) return;
        // A drag that starts on a bend of a straight or curved line moves
        // that bend. Otherwise the drag adds a second bend next to it.
        const at = screenToFlowPosition({ x: event.clientX, y: event.clientY });
        const bends = isElbow ? [] : points.slice(1, -1);
        const bendIndex = bends.findIndex(
          (bend) =>
            Math.hypot(bend.x - at.x, bend.y - at.y) <= BEND_GRAB_DISTANCE
        );
        startDrag(
          (to) => ({
            kind: 'route',
            waypoints:
              bendIndex === -1
                ? dragLineHandle(points, handle, to)
                : moveBend(bends, bendIndex, to),
          }),
          (done) =>
            done.kind === 'route' && editEdge(id, { waypoints: done.waypoints })
        )(event);
      }}
    >
      <ArrowEdge
        {...props}
        data={{
          ...data,
          outlineColor:
            !locked && (selected || labelHovered || hoverCursor)
              ? COLORS.BLUE[100]
              : undefined,
          waypoints: route?.length ? route : data.waypoints,
          edgeLabel: (
            <DiagramLineLabel
              edgeID={id}
              label={label}
              selected={!!selected}
              points={points}
              startDrag={startDrag}
              onHoverChange={setLabelHovered}
            />
          ),
          labelPosition,
        }}
      />
      {selected && (
        <DiagramLineHandles
          edgeID={id}
          points={points}
          isElbow={isElbow}
          labelBox={labelBox}
          startDrag={startDrag}
        />
      )}
    </g>
  );
}

/** Returns `bends` with the bend at `index` moved to `to`, in whole pixels. */
function moveBend(bends: XYPosition[], index: number, to: XYPosition) {
  return bends.map((bend, each) =>
    each === index ? { x: Math.round(to.x), y: Math.round(to.y) } : bend
  );
}

interface DiagramLinePartProps {
  edgeID: string;
  points: XYPosition[];
  startDrag: StartLineDrag;
}

/**
 * The label of a line. The user drags the label along the
 * line, and double-clicks it to edit it in place. A line without a label
 * shows the editor only after the user selects Add label.
 */
function DiagramLineLabel({
  edgeID,
  label,
  selected,
  points,
  startDrag,
  onHoverChange,
}: DiagramLinePartProps & {
  label: string;
  selected: boolean;
  onHoverChange: (hovered: boolean) => void;
}) {
  const { locked, editEdge, labelTarget, openLabelEditor, closeLabelEditor } =
    useDiagramEditing();
  if (locked)
    return label ? <EdgeLabel label={label} classes={LABEL_CLASSES} /> : null;

  if (labelTarget?.kind === 'edge' && labelTarget.id === edgeID) {
    return (
      <Box sx={{ pointerEvents: 'all' }}>
        <EdgeLabel
          classes={LABEL_CLASSES}
          highlight
          editor={
            <InlineTextEditor
              value={label}
              label="Label"
              onCommit={(text) => {
                closeLabelEditor();
                // A line without label text shows no label.
                if (text.trim() !== label) {
                  editEdge(edgeID, { label: text.trim() });
                }
              }}
              onCancel={closeLabelEditor}
            />
          }
        />
      </Box>
    );
  }
  if (!label) return null;
  return (
    <Box
      onPointerEnter={() => onHoverChange(true)}
      onPointerLeave={() => onHoverChange(false)}
      sx={{
        pointerEvents: 'all',
        // This box captures the pointer during a drag, so it owns the cursor.
        cursor: selected ? 'move' : 'default',
        '& button': {
          cursor: 'inherit',
          ...(selected && {
            outline: `1.5px solid ${COLORS.BLUE[600]}`,
            outlineOffset: 2,
          }),
          '&:focus-visible': {
            outline: `1.5px solid ${COLORS.BLUE[600]}`,
            outlineOffset: 2,
          },
        },
      }}
      onDoubleClick={() => openLabelEditor({ kind: 'edge', id: edgeID })}
      onPointerDown={startDrag(
        (point) => ({
          kind: 'label',
          fraction: Math.min(
            1 - LABEL_END_MARGIN,
            Math.max(LABEL_END_MARGIN, getNearestFraction(points, point))
          ),
        }),
        (done) =>
          done.kind === 'label' &&
          editEdge(edgeID, {
            labelPosition: Number(done.fraction.toFixed(3)),
          })
      )}
    >
      <EdgeLabel label={label} classes={LABEL_CLASSES} highlight={selected} />
    </Box>
  );
}

/**
 * The route handles of a selected line. An elbow line has a handle on each
 * inner segment. A straight or curved line has a handle in the middle of
 * each segment, which adds a bend, and a handle on each bend. The line has
 * no handles while the user edits its label. A handle does not cover the
 * label, so the user can read the label and double-click it.
 */
function DiagramLineHandles({
  edgeID,
  points,
  isElbow,
  labelBox,
  startDrag,
}: DiagramLinePartProps & { isElbow: boolean; labelBox?: LineLabelBox }) {
  const { locked, editEdge, labelTarget } = useDiagramEditing();
  if (locked || (labelTarget?.kind === 'edge' && labelTarget.id === edgeID)) {
    return null;
  }
  const saveRoute = (done: LineDrag) =>
    done.kind === 'route' && editEdge(edgeID, { waypoints: done.waypoints });
  // The bends of a straight or curved line are its saved waypoints.
  const bends = isElbow ? [] : points.slice(1, -1);
  return (
    <EdgeLabelRenderer>
      {getLineHandles(points, isElbow, labelBox).map((handle) => (
        <LineControlPoint
          key={`${handle.index}-${handle.axis ?? 'add'}`}
          point={handle.point}
          active={false}
          label={handle.axis ? 'Move line segment' : 'Add a bend'}
          cursor={HANDLE_CURSOR[handle.axis ?? 'add']}
          onPointerDown={startDrag(
            (point) => ({
              kind: 'route',
              waypoints: dragLineHandle(points, handle, point),
            }),
            saveRoute
          )}
        />
      ))}
      {bends.map((bend, index) => (
        <LineControlPoint
          key={`bend-${index}`}
          point={bend}
          active
          label="Move bend"
          cursor={HANDLE_CURSOR.bend}
          onPointerDown={startDrag(
            (point) => ({
              kind: 'route',
              waypoints: moveBend(bends, index, point),
            }),
            saveRoute
          )}
          onMove={(step) =>
            editEdge(edgeID, {
              waypoints: moveBend(bends, index, {
                x: bend.x + step.x,
                y: bend.y + step.y,
              }),
            })
          }
          onRemove={() =>
            editEdge(edgeID, {
              waypoints: bends.filter((_, each) => each !== index),
            })
          }
        />
      ))}
    </EdgeLabelRenderer>
  );
}
